import React, { useMemo } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import { useStyles } from 'ui/base/theme';
import { UnreachableFunction } from 'ui/base/types';
import { Step, StepLabel, Stepper } from 'ui/standard/stepper/stepper';

import { UpdateUpgrade, UpdateUpgradeStatus } from 'services/pangolin/types';

import { themedStyles } from '../manage_updates';

import Messages from './update_upgrade_stepper_messages';

type UpdateUpgradeStepStatus = 'disabled' | 'pending' | 'completed' | 'failed' | 'active';

type UpdateUpgradeStep = 'reboot' | 'install' | 'switch' | 'backup';
const UpdateUpgradeStepValues: Record<UpdateUpgradeStep, number> = {
  reboot: 1,
  install: 2,
  switch: 3,
  backup: 4,
};

export type UpdateUpgradeStepperProps = {
  upgrade?: UpdateUpgrade;
};
export const UpdateUpgradeStepper = observer(({ upgrade }: UpdateUpgradeStepperProps) => {
  const styles = useStyles(themedStyles);

  const step = useMemo(() => {
    if (!upgrade) return undefined;
    switch (upgrade.terminal_status ?? upgrade.status) {
      case UpdateUpgradeStatus.PENDING: return 'reboot';
      case UpdateUpgradeStatus.DOWNLOAD: return 'install';
      case UpdateUpgradeStatus.INSTALL: return 'install';
      case UpdateUpgradeStatus.REBOOT: return 'switch';
      case UpdateUpgradeStatus.LAUNCH: return 'backup';
      case UpdateUpgradeStatus.BACKUP: return 'backup';
      default: return undefined;
    }
  }, [upgrade?.terminal_status, upgrade?.status]);

  const finished = useMemo(() => {
    if (!upgrade) return false;
    switch (upgrade.status) {
      case UpdateUpgradeStatus.COMPLETED: return true;
      case UpdateUpgradeStatus.CANCELLED: return true;
      case UpdateUpgradeStatus.SKIPPED: return true;
      case UpdateUpgradeStatus.FAILED: return true;
      default: return false;
    }
  }, [upgrade?.status]);

  const skipped = useMemo(() => {
    if (!upgrade) return false;
    switch (upgrade.status) {
      case UpdateUpgradeStatus.CANCELLED: return true;
      case UpdateUpgradeStatus.SKIPPED: return true;
      default: return false;
    }
  }, [upgrade?.status]);

  const failed = useMemo(() => {
    if (!upgrade) return false;
    switch (upgrade.status) {
      case UpdateUpgradeStatus.FAILED: return true;
      default: return false;
    }
  }, [upgrade?.status]);

  const stepValue = step ? UpdateUpgradeStepValues[step] : 0;

  const stepStatusReboot: UpdateUpgradeStepStatus = (
    stepValue < 1 ? finished ? 'disabled' : 'pending' :
    stepValue > 1 ? 'completed' : failed ? 'failed' :
    skipped ? 'disabled' : finished ? 'completed' : 'active'
  );
  const stepStatusInstall: UpdateUpgradeStepStatus = (
    stepValue < 2 ? finished ? 'disabled' : 'pending' :
    stepValue > 2 ? 'completed' : failed ? 'failed' :
    skipped ? 'disabled' : finished ? 'completed' : 'active'
  );
  const stepStatusSwitch: UpdateUpgradeStepStatus = (
    stepValue < 3 ? finished ? 'disabled' : 'pending' :
    stepValue > 3 ? 'completed' : failed ? 'failed' :
    skipped ? 'disabled' : finished ? 'completed' : 'active'
  );
  const stepStatusBackup: UpdateUpgradeStepStatus = (
    stepValue < 4 ? finished ? 'disabled' : 'pending' :
    stepValue > 4 ? 'completed' : failed ? 'failed' :
    skipped ? 'disabled' : finished ? 'completed' : 'active'
  );

  const stepPulse = (stepStatus: UpdateUpgradeStepStatus) => {
    return stepStatus === 'active';
  };

  const stepLabel = (stepStatus: UpdateUpgradeStepStatus) => {
    switch (stepStatus) {
      case 'disabled': return Messages.stepLabelSkipped();
      case 'pending': return Messages.stepLabelPending();
      case 'completed': return Messages.stepLabelCompleted();
      case 'active': return Messages.stepLabelInProgress();
      case 'failed': return Messages.stepLabelFailed();
      default: return UnreachableFunction(stepStatus);
    }
  };

  return (
    <Stepper>
      <Step status={stepStatusReboot} pulse={stepPulse(stepStatusReboot)}>
        <StepLabel>
          <div className={classNames(styles.field)}>
            <div className={styles.fieldTextRight}>
              {Messages.stepNameReboot()}
              <div className={styles.fieldInfo}>
                {stepLabel(stepStatusReboot)}
              </div>
            </div>
          </div>
        </StepLabel>
      </Step>
      <Step status={stepStatusInstall} pulse={stepPulse(stepStatusInstall)}>
        <StepLabel>
          <div className={classNames(styles.field)}>
            <div className={styles.fieldTextRight}>
              {Messages.stepNameInstall()}
              <div className={styles.fieldInfo}>
                {stepLabel(stepStatusInstall)}
              </div>
            </div>
          </div>
        </StepLabel>
      </Step>
      <Step status={stepStatusSwitch} pulse={stepPulse(stepStatusSwitch)}>
        <StepLabel>
          <div className={classNames(styles.field)}>
            <div className={styles.fieldTextRight}>
              {Messages.stepNameSwitch()}
              <div className={styles.fieldInfo}>
                {stepLabel(stepStatusSwitch)}
              </div>
            </div>
          </div>
        </StepLabel>
      </Step>
      <Step status={stepStatusBackup} pulse={stepPulse(stepStatusBackup)}>
        <StepLabel>
          <div className={classNames(styles.field)}>
            <div className={styles.fieldTextRight}>
              {Messages.stepNameBackup()}
              <div className={styles.fieldInfo}>
                {stepLabel(stepStatusBackup)}
              </div>
            </div>
          </div>
        </StepLabel>
      </Step>
    </Stepper>
  );
});
